import React, { useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Typography as MuiTypography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { createCommonStyles } from '../theme/styles';

const NotificationItem = ({ 
  notification, 
  index, 
  totalCount,
  isDarkMode, 
  onMarkAsRead, 
  onApproveRequest, 
  onRejectRequest,
  onConfirmAppointment,
  onRejectAppointment,
  onRefreshNotifications
}) => {
  const [openOriginalDialog, setOpenOriginalDialog] = useState(false);
  const commonStyles = createCommonStyles(isDarkMode);

  const handleClick = () => {
    if (notification.type !== 'customer_add_request' && notification.type !== 'appointment_request') {
      onMarkAsRead(notification._id || notification.id);
    }
  };

  // Determine response color based on approval status
  const getResponseColor = () => {
    if (!notification.response) return isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)';
    // exact-match special case: show modified appointment request in yellow
    const respTrim = (notification.response || '').toString().trim();
    if (respTrim === 'Zahtev za termin je izmenjen.' || respTrim === 'Zahtev za termin je izmenjen') {
      return isDarkMode ? '#F59E0B' : '#FBBF24'; // yellow / amber
    }

    const responseText = respTrim.toLowerCase();
    if (responseText.includes('odobren') || responseText.includes('prihvaćen') || responseText.includes('approved') || responseText.includes('potvrđena')) {
      return isDarkMode ? '#10B981' : '#059669'; // Green
    } else if (responseText.includes('odbačen') || responseText.includes('odbijen') || responseText.includes('rejected')) {
      return isDarkMode ? '#EF4444' : '#DC2626'; // Red
    }
    
    return isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'; // Default
  };

  return (
    <MenuItem
      onClick={handleClick}
      sx={{
        bgcolor: !notification.isRead ? (isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)') : 'transparent',
        '&:hover': {
          bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
        },
        cursor: (notification.type === 'customer_add_request' || notification.type === 'appointment_request') ? 'default' : 'pointer'
      }}
    >
      <Box sx={{ width: '100%' }}>
        <MuiTypography 
          variant="body2" 
          sx={{ 
            fontWeight: !notification.isRead ? 600 : 400,
            color: isDarkMode ? 'white' : '#1E293B',
            mb: 0.5,
            whiteSpace: 'pre-wrap'
          }}
        >
          {notification.title || notification.text || 'Obaveštenje'}
        </MuiTypography>
        
        {notification.message && (
          <MuiTypography 
            variant="caption" 
            sx={{ 
              color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
              display: 'block',
              whiteSpace: 'pre-wrap'
            }}
          >
            {notification.message || notification.body}
          </MuiTypography>
        )}
        
        {notification.response && (
          <MuiTypography 
            variant="caption" 
            sx={{ 
              color: getResponseColor(),
              display: 'block',
              mt: 0.5,
              fontStyle: 'italic',
              fontWeight: 500
            }}
          >
            {notification.response}
          </MuiTypography>
        )}
        
        {notification.createdAt && (
          <MuiTypography 
            variant="caption" 
            sx={{ 
              color: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
              display: 'block',
              mt: 0.5
            }}
          >
            {new Date(notification.createdAt).toLocaleString('sr-RS')}
          </MuiTypography>
        )}
        {/* If this is an appointment_request with an originalNotificationId, show a note + details button */}
        {notification.type === 'appointment_request' && notification.originalNotificationId && (
          <>
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenOriginalDialog(true);
                }}
              >
                Vidi detalje
              </Button>
            </Box>

            <Dialog
              open={openOriginalDialog}
              onClose={() => setOpenOriginalDialog(false)}
              fullWidth
              maxWidth="sm"
              PaperProps={{ sx: { ...commonStyles.dialogPaper, borderRadius: 2, overflow: 'hidden' } }}
            >
              <DialogTitle sx={{ ...commonStyles.dialogTitle, fontWeight: 800, fontSize: '1rem' }}>
                Originalni zahtev
              </DialogTitle>
              <DialogContent dividers sx={{ ...commonStyles.dialogContent, p: 3 }}>
                {(() => {
                  const origPara = notification.originalAppointmentText?.split('\n\n')[1] || '';
                  const updPara =  notification.text?.split('\n\n')[1] || '';
                  if (!origPara && !updPara) {
                    return (
                      <MuiTypography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: commonStyles.textHi.color }}>
                        Nema detalja za prikaz.
                      </MuiTypography>
                    );
                  }

                  if (origPara === updPara) {
                    return (
                      <MuiTypography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: commonStyles.textHi.color }}>
                        {origPara || 'Nema detalja za prikaz.'}
                      </MuiTypography>
                    );
                  }

                  return (
                    <MuiTypography
                      variant="body2"
                      component="div"
                      sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: commonStyles.textHi.color }}
                      dangerouslySetInnerHTML={{ __html: notification.originalAppointmentText || '' }}
                    />
                  );
                })()}
              </DialogContent>
              <DialogActions sx={{ ...commonStyles.dialogActions, p: 2 }}>
                <Button
                  onClick={() => setOpenOriginalDialog(false)}
                  variant="contained"
                  sx={{ ...commonStyles.gradientButton, fontWeight: 700 }}
                >
                  Zatvori
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        
        {/* Show approve/reject buttons for customer_add_request notifications only if no response */}
        {(notification.type === 'customer_add_request' || notification.type === 'appointment_request') && !notification.response && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={async (e) => {
                e.stopPropagation();
                // Mark as read first
                onMarkAsRead(notification._id || notification.id);
                // Then approve
                if(notification.type==='customer_add_request'){
                  await onApproveRequest(notification);
                }
                if(notification.type==='appointment_request'){
                  await onConfirmAppointment(notification);
                }
                if (onRefreshNotifications) {
                  onRefreshNotifications();
                }
              }}
              sx={{
                background: 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 2,
                py: 0.5,
                '&:hover': {
                  background: 'linear-gradient(90deg, #059669 0%, #047857 100%)',
                }
              }}
            >
              Prihvati
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={async (e) => {
                e.stopPropagation();
                // Mark as read first
                onMarkAsRead(notification._id || notification.id);
                // Then reject
                if(notification.type==='customer_add_request'){
                  await onRejectRequest(notification);
                }
                if(notification.type==='appointment_request'){
                  await onRejectAppointment(notification);
                }
                if (onRefreshNotifications) {
                  onRefreshNotifications();
                }
              }}
              sx={{
                color: '#EF4444',
                borderColor: '#EF4444',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 2,
                py: 0.5,
                '&:hover': {
                  borderColor: '#DC2626',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                }
              }}
            >
              Odbij
            </Button>
          </Box>
        )}
      </Box>
    </MenuItem>
  );
};

export default NotificationItem;